/** Timeline lane rows run front-to-back, top to bottom — the NLE convention.
 *
 *  Stacking order is the `layers` array order: index 0 is painted first, so it
 *  sits BEHIND everything (see `buildLayersRenderInput`, which derives
 *  `zIndex` from the array index, and the render hosts, which map the array in
 *  order). Row 0 must therefore show the LAST array element, so that dragging a
 *  lane upward brings its layer toward the camera.
 *
 *  The mapping is its own inverse, so the same call converts either way. */
export function laneRowForIndex(index: number, count: number): number {
  return count - 1 - index;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Round halves away from zero. `Math.round` breaks ties toward +Infinity
 *  (`Math.round(-0.5) === -0`), which would make an upward drag need one pixel
 *  more travel than a downward one to cross the same lane boundary. */
const roundHalfAwayFromZero = (v: number) => Math.sign(v) * Math.round(Math.abs(v));

/** Target array index for a lane dragged `dyPx` vertically from `startIndex`.
 *
 *  Rounding to the nearest lane means the layer only changes rows once the
 *  pointer passes a lane's midpoint — that half-lane hysteresis is what keeps
 *  an ordinary horizontal (time) drag from restacking anything by accident, so
 *  no separate threshold is needed. Ends clamp rather than wrap. */
export function resolveLaneReorder(
  { startIndex, count, dyPx, laneHeight }:
  { startIndex: number; count: number; dyPx: number; laneHeight: number },
): number {
  if (count <= 1 || !Number.isFinite(dyPx) || laneHeight <= 0) return startIndex;
  const startRow = laneRowForIndex(startIndex, count);
  const row = clamp(startRow + roundHalfAwayFromZero(dyPx / laneHeight), 0, count - 1);
  return laneRowForIndex(row, count);
}
